import { useEffect, useRef } from "react";

/**
 * Toast type enumeration for different toast notification styles
 * @readonly
 * @enum {string}
 */
export const ToastType = Object.freeze({
    /** Informational toast type */
    Informational: "informational",
    /** Success toast type */
    Success: "success"
});

/**
 * Toast component for displaying temporary notification messages
 * 
 * @component
 * @param {Object} props - The component props
 * @param {string} props.heading - The main heading text for the toast
 * @param {string} [props.subtext] - Optional subtext to display below the heading
 * @param {ToastType} [props.type=ToastType.Informational] - The type of toast (informational or success)
 * @param {string} [props.timeLimit=""] - Time limit for auto-dismissal (in seconds)
 * @param {boolean} [props.visible=false] - Controls the visibility of the toast
 * @returns {JSX.Element} The rendered toast component
 * 
 * @example
 * // Basic informational toast
 * <Toast 
 *   heading="Information" 
 *   subtext="This is additional information" 
 *   visible={true} 
 * />
 * 
 * @example
 * // Success toast with custom time limit (10 seconds)
 * <Toast 
 *   heading="Success!" 
 *   type={ToastType.Success}
 *   timeLimit="10"
 *   visible={showToast} 
 * />
 */
export default function Toast({
    heading,
    subtext,
    type = ToastType.Informational,
    timeLimit = "",
    visible = false,
    trackingId
}) {
    /** @type {React.MutableRefObject<HTMLElement>} Reference to the gds-toast element */
    const toastRef = useRef();

    /**
     * Effect hook to handle toast visibility
     * Sets the 'visible' attribute on the toast element when the visible prop is true
     */
    useEffect(() => {
        if (visible) {
            toastRef.current?.setAttribute("visible", "");
        }
    }, [visible]);

    return (
        <gds-toast data-onboarding-control-id={trackingId} ref={toastRef} type={type} time-limit={timeLimit}>
            {heading}
            {subtext && <span slot="toast-subtext">{subtext}</span>}
        </gds-toast>
    );
}